import * as React from 'react';

import {
    ApplicationHelpType,
    LandingPageSettingEnum,
    UserSettingTypesEnum,
} from '../../utilities/Constants';
import { Link, useNavigate, useParams } from 'react-router-dom';

import CheckmateApiClient from '../../services/apiClient';
import { CheckmateDialog } from '../../components/shared/dialog';
import { DisplayMessages } from '../../utilities/DisplayMessages';
import { Help } from '../../components/shared/Help';
import { IDialogModel } from '../../interfaces/IDialog';
import { ILoginUser } from '../../interfaces/IAccount';
import { IUserSettingModel } from '../../interfaces/ITask';
import { IValidation } from '../../interfaces/IError';
import { Loader } from '../../components/shared/Loader';
import { LocalRoutes } from '../../utilities/LocalRoutes';
import { MailToSystemAdminSupportLink } from '../../components/shared/MailToSystemAdminSupportLink';
import { OneTimeCodeModal } from './OneTimeCodeModal';
import { PublicAnnouncements } from '../../components/shared/announcements/PublicAnnoucements';
import { UseQueryResult } from 'react-query';
import ValidateUtils from '../../shared/validations';
import classNames from 'classnames';
import { getHotkeyHandler } from '@mantine/hooks';
import { useAuthContext } from '../../contexts/AuthContext';
import { useGetUserSetting } from '../../components/user-settings/useGetUserSetting';
import { useLoading } from '../../contexts/LoadingContext';
import useLocalStorage from '../../hooks/useLocalStorage';

const _apiClient = new CheckmateApiClient();

interface IUserAgreementContentDialog extends IDialogModel {
    userAgreementContentGuid: string;
}

export function AccountLogin() {
    const { guid: invitationGuid } = useParams();
    const { login, isLoggedIn } = useAuthContext();
    const navigate = useNavigate();
    const loading = useLoading();
    const [, , removeUserId] = useLocalStorage('userId', '');

    const [animate, setAnimate] = React.useState<boolean>(false);
    const [animate2, setAnimate2] = React.useState<boolean>(false);
    const [loginUser, setLoginUser] = React.useState<ILoginUser>({});
    const [validation, setValidation] = React.useState<IValidation>({});
    const [displayMessageDialog, setDisplayMessageDialog] = React.useState<IDialogModel>({
        isShowingModal: false,
    });
    const [displayTermsOfServiceDialog, setDisplayTermsOfServiceDialog] =
        React.useState<IUserAgreementContentDialog>({
            isShowingModal: false,
            userAgreementContentGuid: '',
        });
    const [userId, setUserId] = React.useState<string>('');
    const [displayOneTimeCodeDialog, setDisplayOneTimeCodeDialog] = React.useState<boolean>(false);

    const landingPageSetting = useGetUserSetting(
        UserSettingTypesEnum.PreferredLandingPage,
        isLoggedIn
    );
    const preferredZoneSetting = useGetUserSetting(UserSettingTypesEnum.PreferredZone, isLoggedIn);

    const isLoadingUserSettings = preferredZoneSetting.isLoading || landingPageSetting.isLoading;

    const changeValue = (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
    ) => {
        const { name, value } = event.target;
        setChangedValue(name, value);
    };

    const setChangedValue = (name: string, value: any) => {
        const localLoginUser: ILoginUser = { ...loginUser };
        switch (name) {
            case 'email': {
                localLoginUser.userName = value;
                validation.username = [];
                break;
            }
            case 'password':
                localLoginUser.password = value;
                validation.password = [];
                validation.model = [];
                break;
            default:
                break;
        }

        setLoginUser(localLoginUser);
        setValidation(validation);
    };

    const handleTwoFactorAuthenticationClose = () => {
        let redirectTo = LocalRoutes.Root;
        if (invitationGuid) {
            redirectTo = LocalRoutes.AcceptInvitation.replace(':guid', invitationGuid);
        }

        navigate(redirectTo);
    };

    const handleLogin = async () => {
        if (loginUser.userName && loginUser.userName.length > 256) {
            setDisplayMessageDialog({
                isShowingModal: true,
                title: ' ',
                body: 'User Name cannot exceed 256 characters ',
                dialogClassName: 'account-update-dialog ',
                cancelText: 'OK',
                closeButtonClassName: 'btn btn-default float-end',
            });
            return;
        }

        if (loginUser.password && loginUser.password.length > 256) {
            setDisplayMessageDialog({
                isShowingModal: true,
                title: ' ',
                body: 'User Password cannot exceed 256 characters ',
                dialogClassName: 'account-update-dialog',
                cancelText: 'OK',
                closeButtonClassName: 'btn btn-default float-end',
            });

            return;
        }

        if (invitationGuid !== undefined) {
            const invitationResponse = await _apiClient.loginAcceptInvitation(
                loginUser,
                invitationGuid
            );
            if (invitationResponse.errorMessage) {
                setValidation(
                    ValidateUtils.parseErrors(
                        invitationResponse.errors,
                        invitationResponse.errorMessage
                    )
                );
            } else {
                setDisplayMessageDialog({
                    isShowingModal: true,
                    title: ' ',
                    body: 'User Account updated successfully. ',
                    dialogClassName: 'account-update-dialog ',
                    cancelText: 'OK ',
                    closeButtonClassName: 'btn btn-default float-end ',
                });
            }
            return;
        }

        await login(loginUser)
            .then((loginResponse) => {
                if (loginResponse.payload?.invalidTwoFactorVerification) {
                    setDisplayMessageDialog({
                        isShowingModal: true,
                        title: ' ',
                        body: DisplayMessages.TwoFactorTokenInvalid,
                        cancelText: 'OK ',
                        dialogClassName: 'login-username-info-dialog ',
                        closeButtonClassName: 'btn btn-default',
                    });

                    setDisplayOneTimeCodeDialog(false);
                    setUserId(loginResponse.payload.userId);
                    return;
                }

                if (loginResponse.payload?.requiresTwoFactorVerification) {
                    setDisplayOneTimeCodeDialog(true);
                    setUserId(loginResponse.payload.userId);
                    return;
                }

                if (loginResponse.payload?.userAgreementContentGuid) {
                    setDisplayTermsOfServiceDialog({
                        isShowingModal: true,
                        title: 'User Acknowledgment',
                        body: loginResponse.payload.userAgreementContentText.replace(/\\n/g, '\n'),
                        dialogClassName: 'account-accept-terms-dialog ',
                        confirmText: 'I Accept ',
                        cancelText: 'Decline ',
                        closeButtonClassName: 'btn btn-default',
                        confirmButtonClassName: 'btn btn-orange',
                        userAgreementContentGuid: loginResponse.payload.userAgreementContentGuid,
                        size: 'lg',
                    });

                    setUserId(loginResponse.payload.userId);
                    return;
                }

                if (loginResponse.errorMessage) {
                    setValidation(
                        ValidateUtils.parseErrors(loginResponse.errors, loginResponse.errorMessage)
                    );
                }
            })
            .catch((e) => {
                setValidation({ model: [(e as Error).message] });
            });
    };

    const handleAcceptTermsOfService = async () => {
        if (!userId) return;
        loading.showLoading();
        await _apiClient
            .acceptTermsOfService(
                userId,
                displayTermsOfServiceDialog.userAgreementContentGuid,
                loginUser
            )
            .then((response) => {
                if (response.httpResponse.status === 200) {
                    handleLogin();
                }
            })
            .finally(() => {
                loading.hideLoading();
                setDisplayTermsOfServiceDialog((prev) => ({ ...prev, isShowingModal: false }));
            });
    };

    const handleDisplayMessageConfirm = () => {
        const displayMessageDialogCopy = { ...displayMessageDialog };
        displayMessageDialogCopy.isShowingModal = false;
        setDisplayMessageDialog(displayMessageDialogCopy);
    };

    const handleRejectTermsOfService = () => {
        const displayRequiredDialogCopy = { ...displayTermsOfServiceDialog };
        const validationCopy = { ...validation };

        displayRequiredDialogCopy.isShowingModal = false;
        validation.termsOfService = ['rejected'];

        setDisplayMessageDialog({
            isShowingModal: true,
            title: ' ',
            bodyComponent: (
                <div>
                    <div>{`${DisplayMessages.AcceptTermsOfServiceRequiredLogin}`}</div>
                    <br />
                    <div>
                        {' '}
                        {DisplayMessages.AcceptTermsOfServiceEmailPrefix}{' '}
                        <MailToSystemAdminSupportLink />{' '}
                        {DisplayMessages.AcceptTermsOfServiceEmailSuffix}
                    </div>
                </div>
            ),
            cancelText: 'OK',
            dialogClassName: 'login-username-info-dialog',
            closeButtonClassName: 'btn btn-default',
        });
        setDisplayTermsOfServiceDialog(displayRequiredDialogCopy);
        setValidation(validationCopy);
    };

    const handle2FactorSuccess = () => {
        setDisplayOneTimeCodeDialog(false);
        handleLogin();
    };

    const handleLandingPageRedirect = ({
        landingPageSetting,
        preferredZoneSetting,
    }: {
        landingPageSetting: UseQueryResult<IUserSettingModel | undefined, unknown>;
        preferredZoneSetting: UseQueryResult<IUserSettingModel | undefined, unknown>;
    }) => {
        const shouldRedirect =
            landingPageSetting?.data &&
            !landingPageSetting?.isLoading &&
            preferredZoneSetting?.data &&
            !preferredZoneSetting?.isLoading;

        if (!shouldRedirect) return;

        const landingPageType = landingPageSetting.data?.settingsJson;
        const preferredZoneGuid = preferredZoneSetting.data?.settingsJson;

        let redirectUrl = LocalRoutes.QuadDashboard;

        if (landingPageType === LandingPageSettingEnum.PreferredZone.toString()) {
            redirectUrl = LocalRoutes.ZoneRoot + preferredZoneGuid;
        }

        setAnimate(true);
        setTimeout(() => {
            setAnimate2(true);
        }, 250);

        setTimeout(() => {
            navigate(redirectUrl, { state: { isAuthenticated: true } });
        }, 1000);
    };

    React.useEffect(() => {
        removeUserId();
    }, []);

    React.useEffect(() => {
        if (isLoggedIn && !isLoadingUserSettings) {
            if (landingPageSetting.data && preferredZoneSetting.data) {
                handleLandingPageRedirect({
                    landingPageSetting,
                    preferredZoneSetting,
                });
            } else {
                navigate(LocalRoutes.QuadDashboard, { state: { isAuthenticated: isLoggedIn } });
            }
        }
    }, [isLoadingUserSettings, isLoggedIn, landingPageSetting, preferredZoneSetting]);

    return (
        <>
            <PublicAnnouncements />
            <div className="login-container">
                {isLoadingUserSettings && <Loader />}
                <img className="app-logo" />
                <svg className="login-background">
                    <g
                        className={classNames('login-translation', {
                            'set-transform-translate': animate2,
                        })}
                    >
                        <g
                            className={classNames('login-rotation', {
                                'set-transform-rotate': animate2,
                            })}
                        >
                            <path
                                d="M 0,0 L 1,0 1,1 0,1 Z M 0,0 L -1,0 -1,-1 0,-1 Z"
                                className="login-check"
                            />
                        </g>
                    </g>
                </svg>
                <div
                    className={classNames('login-panel col-sm-2 px-3', {
                        'set-opacity': animate,
                    })}
                >
                    <div className="logo-container">
                        <img className="logo-img rounded" src="/images/ckmt_login.svg" />
                    </div>
                    <div className="form-group">
                        {(validation.model?.length ?? 0) > 0 && (
                            <div className="text-danger mt-2">{validation.model}</div>
                        )}
                        <label className="col-form-label">
                            Username*
                            <Help
                                type={ApplicationHelpType.Info}
                                title="Username"
                                helpText={DisplayMessages.LoginUsernameInfo}
                            />
                        </label>
                        <input
                            className="form-control"
                            type="text"
                            name="email"
                            value={loginUser.userName || ''}
                            onChange={changeValue}
                        />
                        <span className="text-danger">{validation.username}</span>
                    </div>
                    <div className="form-group">
                        <label className="col-form-label">Password*</label>
                        <input
                            className="form-control"
                            type="password"
                            name="password"
                            value={loginUser.password || ''}
                            onChange={changeValue}
                            onKeyDown={getHotkeyHandler([['Enter', handleLogin]])}
                        />
                        <span className="text-danger">{validation.password}</span>
                        <Link className="float-end" to={LocalRoutes.ForgotPassword}>
                            Forgot password?
                        </Link>
                    </div>
                    <div className="form-group">
                        <button
                            className="btn btn-black full-width vertical-margin-lg"
                            onClick={handleLogin}
                        >
                            Login
                        </button>
                    </div>
                    <CheckmateDialog
                        isShowingModal={displayMessageDialog.isShowingModal}
                        title={displayMessageDialog.title}
                        body={displayMessageDialog.body}
                        handleClose={handleDisplayMessageConfirm}
                        dialogClassName={displayMessageDialog.dialogClassName}
                        cancelText={displayMessageDialog.cancelText}
                        closeButtonClassName={displayMessageDialog.closeButtonClassName}
                        showBackdrop
                        bodyComponent={displayMessageDialog.bodyComponent}
                    />

                    <CheckmateDialog
                        isShowingModal={displayTermsOfServiceDialog.isShowingModal}
                        title={displayTermsOfServiceDialog.title}
                        body={displayTermsOfServiceDialog.body}
                        handleClose={handleRejectTermsOfService}
                        handleConfirm={handleAcceptTermsOfService}
                        dialogClassName={displayTermsOfServiceDialog.dialogClassName}
                        confirmText={displayTermsOfServiceDialog.confirmText}
                        cancelText={displayTermsOfServiceDialog.cancelText}
                        closeButtonClassName={displayTermsOfServiceDialog.closeButtonClassName}
                        confirmButtonClassName={displayTermsOfServiceDialog.confirmButtonClassName}
                        showBackdrop
                        size={displayTermsOfServiceDialog.size}
                    />

                    <OneTimeCodeModal
                        loginUser={loginUser}
                        onClose={handleTwoFactorAuthenticationClose}
                        onSuccess={handle2FactorSuccess}
                        open={displayOneTimeCodeDialog}
                    />
                </div>
                <div className={classNames('login-sidebar', { 'set-opacity': animate })}></div>
            </div>
        </>
    );
}
